// Utility functions for ExecutionTester
import {
  parseJinja2Variables,
  generateJinja2Preview,
  validateJinja2Variables,
  getJinja2TemplateStats,
} from './jinja2Utils';

// Get execution status info
export const getExecutionStatusInfo = execution => {
  if (execution.success === false) {
    return { color: 'error', label: 'Failed' };
  }
  if (execution.success === true && execution.raw_response) {
    return { color: 'success', label: 'Completed' };
  }
  if (execution.executing) {
    return { color: 'warning', label: 'Running' };
  }
  if (execution.error_message) {
    return { color: 'error', label: 'Error' };
  }
  return { color: 'info', label: 'Pending' };
};

// Format execution time
export const formatExecutionTime = execution => {
  if (!execution.execution_time) return 'N/A';
  return `${execution.execution_time.toFixed(2)}s`;
};

// Helper function to validate variable names (same logic as jinja2Utils)
const isValidVariableName = variableName => {
  if (!variableName || typeof variableName !== 'string') {
    return false;
  }

  const trimmed = variableName.trim();

  // Empty strings are invalid
  if (trimmed.length === 0) {
    return false;
  }

  // Filter out escaped syntax patterns (with or without quotes)
  const escapedPatterns = [
    "'{{'",
    "'}}'",
    "'{{",
    "}}'",
    "{{'",
    "}}'",
    '{{',
    '}}',
    "'",
    '"',
  ];

  // Check exact matches
  if (escapedPatterns.includes(trimmed)) {
    return false;
  }

  // Filter out variables that start or end with quotes (likely escaped syntax)
  if (trimmed.startsWith("'") || trimmed.endsWith("'")) {
    return false;
  }
  if (trimmed.startsWith('"') || trimmed.endsWith('"')) {
    return false;
  }

  // Filter out variables that contain curly braces (likely escaped syntax)
  if (trimmed.includes('{{') || trimmed.includes('}}')) {
    return false;
  }

  // Filter out single character special characters
  if (trimmed.length === 1 && !/^[a-zA-Z_0-9]$/.test(trimmed)) {
    return false;
  }

  // Valid variable names should start with a letter or underscore
  // and contain only letters, numbers, underscores, and dots
  const validVariablePattern = /^[a-zA-Z_][a-zA-Z0-9_.]*$/;
  if (!validVariablePattern.test(trimmed)) {
    return false;
  }

  return true;
};

// Validate template variables
export const validateTemplateVariables = (template, executionData) => {
  if (!template?.template) return { isValid: true, missingVariables: [] };

  try {
    const variableRegex = /\{\{([^}]+)\}\}/g;
    const variables = [];
    let match;

    while ((match = variableRegex.exec(template.template)) !== null) {
      const variableName = match[1]?.trim();
      if (
        variableName &&
        typeof variableName === 'string' &&
        isValidVariableName(variableName) &&
        !variables.includes(variableName)
      ) {
        variables.push(variableName);
      }
    }

    const missingVariables = variables.filter(
      variable =>
        variable &&
        typeof variable === 'string' &&
        (!executionData.input_data[variable] ||
          executionData.input_data[variable].trim() === '')
    );

    return {
      isValid: missingVariables.length === 0,
      missingVariables,
    };
  } catch (error) {
    // Error validating template variables
    return { isValid: false, missingVariables: [] };
  }
};

// Clean input data to ensure no object values
export const cleanInputData = inputData => {
  if (!inputData || typeof inputData !== 'object') return {};

  const cleanData = {};
  Object.entries(inputData).forEach(([key, value]) => {
    if (key && typeof key === 'string') {
      cleanData[key] = typeof value === 'object' ? String(value) : value;
    }
  });

  return cleanData;
};

// Generate template preview (legacy function for backward compatibility)
export const generateTemplatePreview = (template, inputData) => {
  if (!template?.template) return 'Template not loaded';

  try {
    let preview = String(template.template);

    if (
      inputData &&
      typeof inputData === 'object' &&
      Object.keys(inputData).length > 0
    ) {
      Object.entries(inputData).forEach(([key, value]) => {
        if (key && typeof key === 'string' && value !== undefined) {
          try {
            const regex = new RegExp(`\\{\\{${key}\\}\\}`, 'g');
            const safeValue = typeof value === 'object' ? String(value) : value;
            preview = preview.replace(regex, String(safeValue) || `{{${key}}}`);
          } catch (regexError) {
            // Regex error for key
          }
        }
      });
    }

    return preview || 'No preview available';
  } catch (error) {
    // Template preview error
    return 'Error generating preview';
  }
};

// Enhanced template preview with Jinja2 support
export const generateTemplatePreviewEnhanced = (template, inputData) => {
  if (!template?.template) return 'Template not loaded';

  try {
    // Use Jinja2-enhanced preview if available
    return generateJinja2Preview(template.template, inputData);
  } catch (error) {
    // Fallback to basic preview
    return generateTemplatePreview(template, inputData);
  }
};

// Enhanced variable parsing with Jinja2 support
export const parseTemplateVariablesEnhanced = templateContent => {
  if (!templateContent) return [];

  try {
    // Use Jinja2-enhanced parsing if available (which now includes filtering)
    return parseJinja2Variables(templateContent);
  } catch (error) {
    // Fallback to basic regex parsing with validation
    const variableRegex = /\{\{([^}]+)\}\}/g;
    const variables = [];
    let match;

    while ((match = variableRegex.exec(templateContent)) !== null) {
      const variableName = match[1]?.trim();
      if (
        variableName &&
        typeof variableName === 'string' &&
        isValidVariableName(variableName) &&
        !variables.includes(variableName)
      ) {
        variables.push(variableName);
      }
    }

    return variables;
  }
};

// Enhanced validation with Jinja2 support
export const validateTemplateVariablesEnhanced = (template, executionData) => {
  if (!template?.template) return { isValid: true, missingVariables: [] };

  try {
    // Use Jinja2-enhanced validation if available
    return validateJinja2Variables(template, executionData);
  } catch (error) {
    // Fallback to basic validation
    return validateTemplateVariables(template, executionData);
  }
};

// Get template complexity and Jinja2 features
export const getTemplateComplexity = templateContent => {
  if (!templateContent) {
    return {
      hasConditionals: false,
      hasLoops: false,
      hasFilters: false,
      variableCount: 0,
      complexity: 'simple',
    };
  }

  try {
    return getJinja2TemplateStats(templateContent);
  } catch (error) {
    return {
      hasConditionals: false,
      hasLoops: false,
      hasFilters: false,
      variableCount: 0,
      complexity: 'simple',
    };
  }
};
